import React, { useState, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { X, CheckCircle2, Loader2 } from 'lucide-react';
import { sendDemoEmail, DemoFormData } from '../lib/sendDemoEmail';
const roles = ['Mill Owner / Manufacturer', 'Trader / Broker', 'Exporter / Importer', 'Retailer / Brand', 'Investor', 'Other'];
function DemoModal({
  onClose
}: {
  onClose: () => void;
}) {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState<DemoFormData>({
    name: '',
    email: '',
    phone: '',
    role: '',
    company: ''
  });
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await sendDemoEmail(form);
      setSubmitted(true);
    } catch (err) {
      setError('Something went wrong. Please try again or email us directly.');
    } finally {
      setLoading(false);
    }
  };
  return <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} data-id="element-98">
      {/* Backdrop */}
      <motion.div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} data-id="element-99" />

      {/* Modal */}
      <motion.div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" initial={{
      opacity: 0,
      scale: 0.94,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.94,
      y: 20
    }} transition={{
      ease: [0.34, 1.56, 0.64, 1],
      duration: 0.4
    }} data-id="element-100">
        <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 rounded-full bg-[#F5F4F0] flex items-center justify-center hover:bg-[#E8E4DC] transition-colors z-10" data-id="element-101">
          <X className="w-4 h-4 text-[#5C5A55]" data-id="element-102" />
        </button>

        {!submitted ? <div className="p-8" data-id="element-103">
            <div className="mb-6" data-id="element-104">
              <h2 className="font-jakarta text-2xl font-extrabold text-[#0F0E0C] mb-1" data-id="element-105">
                Request a Demo
              </h2>
              <p className="text-sm text-[#9B9890] font-inter" data-id="element-106">
                Our team will get in touch with you shortly.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4" data-id="element-107">
              <div data-id="element-108">
                <label className="block text-xs font-inter font-semibold text-[#5C5A55] mb-1.5 uppercase tracking-wide" data-id="element-109">
                  Full Name *
                </label>
                <input type="text" required value={form.name} onChange={e => setForm({
              ...form,
              name: e.target.value
            })} placeholder="Rahul Sharma" className="w-full px-4 py-2.5 rounded-xl border border-[#E8E4DC] bg-[#FAFAF8] text-[#0F0E0C] text-sm font-inter focus:outline-none focus:border-[#E8500A] focus:ring-2 focus:ring-[#E8500A]/10 transition-all" data-id="element-110" />
              </div>
              <div data-id="element-111">
                <label className="block text-xs font-inter font-semibold text-[#5C5A55] mb-1.5 uppercase tracking-wide" data-id="element-112">
                  Email *
                </label>
                <input type="email" required value={form.email} onChange={e => setForm({
              ...form,
              email: e.target.value
            })} placeholder="rahul@company.com" className="w-full px-4 py-2.5 rounded-xl border border-[#E8E4DC] bg-[#FAFAF8] text-[#0F0E0C] text-sm font-inter focus:outline-none focus:border-[#E8500A] focus:ring-2 focus:ring-[#E8500A]/10 transition-all" data-id="element-113" />
              </div>
              <div data-id="element-114">
                <label className="block text-xs font-inter font-semibold text-[#5C5A55] mb-1.5 uppercase tracking-wide" data-id="element-115">
                  Phone Number *
                </label>
                <input type="tel" required value={form.phone} onChange={e => setForm({
              ...form,
              phone: e.target.value
            })} placeholder="+91 98765 43210" className="w-full px-4 py-2.5 rounded-xl border border-[#E8E4DC] bg-[#FAFAF8] text-[#0F0E0C] text-sm font-inter focus:outline-none focus:border-[#E8500A] focus:ring-2 focus:ring-[#E8500A]/10 transition-all" data-id="element-116" />
              </div>
              <div data-id="element-117">
                <label className="block text-xs font-inter font-semibold text-[#5C5A55] mb-1.5 uppercase tracking-wide" data-id="element-118">
                  Role *
                </label>
                <select required value={form.role} onChange={e => setForm({
              ...form,
              role: e.target.value
            })} className="w-full px-4 py-2.5 rounded-xl border border-[#E8E4DC] bg-[#FAFAF8] text-[#0F0E0C] text-sm font-inter focus:outline-none focus:border-[#E8500A] focus:ring-2 focus:ring-[#E8500A]/10 transition-all appearance-none" data-id="element-119">
                  <option value="" data-id="element-120">Select your role</option>
                  {roles.map(r => <option key={r} value={r} data-id="element-121">
                      {r}
                    </option>)}
                </select>
              </div>
              <div data-id="element-122">
                <label className="block text-xs font-inter font-semibold text-[#5C5A55] mb-1.5 uppercase tracking-wide" data-id="element-123">
                  Company
                </label>
                <input type="text" value={form.company} onChange={e => setForm({
              ...form,
              company: e.target.value
            })} placeholder="Your company name" className="w-full px-4 py-2.5 rounded-xl border border-[#E8E4DC] bg-[#FAFAF8] text-[#0F0E0C] text-sm font-inter focus:outline-none focus:border-[#E8500A] focus:ring-2 focus:ring-[#E8500A]/10 transition-all" data-id="element-124" />
              </div>

              {error && <p className="text-xs text-red-500 font-inter bg-red-50 px-3 py-2 rounded-lg" data-id="element-125">
                  {error}
                </p>}

              <motion.button type="submit" disabled={loading} whileHover={!loading ? {
            scale: 1.02
          } : {}} whileTap={!loading ? {
            scale: 0.98
          } : {}} className="w-full py-3 bg-[#E8500A] hover:bg-[#D14508] disabled:opacity-60 disabled:cursor-not-allowed text-white font-inter font-semibold rounded-xl transition-colors mt-2 flex items-center justify-center gap-2" data-id="element-126">
                {loading ? <>
                    <Loader2 className="w-4 h-4 animate-spin" data-id="element-127" />
                    Sending...
                  </> : 'Request Demo'}
              </motion.button>
            </form>
          </div> : <div className="p-8 text-center" data-id="element-128">
            <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 15
        }} className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4" data-id="element-129">
              <CheckCircle2 className="w-8 h-8 text-green-600" data-id="element-130" />
            </motion.div>
            <h3 className="font-jakarta text-xl font-extrabold text-[#0F0E0C] mb-2" data-id="element-131">
              Request Received!
            </h3>
            <p className="text-sm text-[#5C5A55] font-inter leading-relaxed mb-6" data-id="element-132">
              Our team will get in touch with you shortly. We look forward to
              showing you The Yarn Bazaar.
            </p>
            <motion.button onClick={onClose} whileHover={{
          scale: 1.02
        }} whileTap={{
          scale: 0.98
        }} className="px-6 py-2.5 bg-[#0F0E0C] text-white font-inter font-semibold rounded-xl text-sm" data-id="element-133">
              Close
            </motion.button>
          </div>}
      </motion.div>
    </motion.div>;
}
export function CTA() {
  const [modalOpen, setModalOpen] = useState(false);
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  return <>
      <section ref={ref} className="py-24 bg-[#FAFAF8]" data-id="element-134">
        <div className="max-w-6xl mx-auto px-5 lg:px-8" data-id="element-135">
          <div className="relative rounded-3xl overflow-hidden bg-[#0F0E0C] px-8 py-20 md:px-16 text-center" data-id="element-136">
            {/* Subtle background texture */}
            <div className="absolute inset-0 thread-pattern opacity-30" data-id="element-137" />
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#E8500A]/20 rounded-full blur-3xl" data-id="element-138" />
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#F97316]/15 rounded-full blur-3xl" data-id="element-139" />

            <div className="relative z-10 max-w-2xl mx-auto" data-id="element-140">
              <motion.p initial={{
              opacity: 0,
              y: 12
            }} animate={isInView ? {
              opacity: 1,
              y: 0
            } : {}} transition={{
              duration: 0.5
            }} className="text-xs font-inter font-semibold text-[#E8500A] tracking-[0.18em] uppercase mb-4" data-id="element-141">
                Get Started
              </motion.p>
              <motion.h2 initial={{
              opacity: 0,
              y: 24,
              filter: 'blur(6px)'
            }} animate={isInView ? {
              opacity: 1,
              y: 0,
              filter: 'blur(0px)'
            } : {}} transition={{
              duration: 0.7,
              delay: 0.05
            }} className="font-jakarta text-4xl md:text-5xl font-extrabold text-white mb-5 leading-tight" data-id="element-142">
                Ready to transform your yarn business?
              </motion.h2>
              <motion.p initial={{
              opacity: 0,
              y: 16
            }} animate={isInView ? {
              opacity: 1,
              y: 0
            } : {}} transition={{
              delay: 0.15,
              duration: 0.6
            }} className="text-base text-white/60 font-inter mb-10 max-w-xl mx-auto leading-relaxed" data-id="element-143">
                Join thousands of mills, traders, and exporters already on The
                Yarn Bazaar. See how we can streamline your supply chain.
              </motion.p>

              <motion.div initial={{
              opacity: 0,
              scale: 0.94
            }} animate={isInView ? {
              opacity: 1,
              scale: 1
            } : {}} transition={{
              delay: 0.25,
              type: 'spring',
              stiffness: 200,
              damping: 20
            }} data-id="element-144">
                <motion.button onClick={() => setModalOpen(true)} whileHover={{
                scale: 1.04
              }} whileTap={{
                scale: 0.97
              }} className="magnetic-btn inline-flex items-center gap-2 px-8 py-4 bg-[#E8500A] hover:bg-[#D14508] text-white font-jakarta font-bold rounded-full text-base transition-colors shadow-lg shadow-[#E8500A]/30" data-id="element-145">
                  Request Demo
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-146">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" data-id="element-147" />
                  </svg>
                </motion.button>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <AnimatePresence data-id="element-148">
        {modalOpen && <DemoModal onClose={() => setModalOpen(false)} data-id="element-149" />}
      </AnimatePresence>
    </>;
}